/*
* Copyright (c) 2023 Eugenio Espinoza
*/

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ArtistProfile.css';
import { useItunesReleases, formatReleaseDate } from '../../../hooks/useItunesReleases';

function ArtistProfile({ artist }) {
  const [showFull, setShowFull] = useState(false);

  const { latest: latestRelease, releases: allReleases } = useItunesReleases(
    artist && artist.appleArtistId ? [artist.appleArtistId] : [],
    6
  );

  if (!artist) return null;

  const {
    name, displayFirst, displayItalic, portrait, city, genre, year, handle, tagline,
    bioLead, bioMore = [], socials = {}, youtubeEmbed, spotifyEmbed, booking,
  } = artist;

  const socialIcons = [
    socials.instagram && { k: 'instagram', href: socials.instagram, label: 'Instagram' },
    socials.facebook && { k: 'facebook', href: socials.facebook, label: 'Facebook' },
    socials.youtube && { k: 'youtube', href: socials.youtube, label: 'YouTube' },
    socials.spotify && { k: 'spotify', href: socials.spotify, label: 'Spotify' },
    socials.apple && { k: 'apple', href: socials.apple, label: 'Apple Music' },
  ].filter(Boolean);

  return (
    <article className="mt-artist">
      <div className="mt-artist__crumbs mt-container">
        <Link to="/artists">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M19 12H5M11 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>The Roster</span>
        </Link>
        <span className="mt-artist__slug">{String(year || '')}</span>
      </div>

      <header className="mt-artist__hero mt-container">
        <div className="mt-artist__portrait">
          <img src={portrait} alt={name} loading="eager" />
          <span className="mt-artist__stamp">MT · {String(year || '')}</span>
        </div>

        <div className="mt-artist__intro">
          <span className="mt-eyebrow">Artist File N° 0{(artist.slug || 'x').toString().length}</span>
          <h1 className="mt-artist__name">
            <span>{displayFirst || name}</span>
            {displayItalic && <em>{displayItalic}</em>}
          </h1>
          {tagline && <p className="mt-artist__tagline">{tagline}</p>}

          <dl className="mt-artist__facts">
            <div><dt>Ciudad</dt><dd>{city || '—'}</dd></div>
            <div><dt>Género</dt><dd>{genre || '—'}</dd></div>
            <div>
              <dt>Instagram</dt>
              <dd>
                {socials.instagram ? (
                  <a href={socials.instagram} target="_blank" rel="noopener noreferrer">{handle || name}</a>
                ) : (handle || '—')}
              </dd>
            </div>
            <div>
              <dt>Booking</dt>
              <dd><a href={`mailto:${booking}`}>{booking}</a></dd>
            </div>
          </dl>
        </div>
      </header>

      <section className="mt-artist__bio mt-container">
        <span className="mt-kicker">Biografía</span>
        <p className="mt-artist__lead">{bioLead}</p>

        <div className={`mt-artist__more ${showFull ? 'is-open' : ''}`}>
          {bioMore.filter(Boolean).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {bioMore.length > 0 && (
          <button className="mt-artist__toggle" onClick={() => setShowFull((v) => !v)}>
            <span>{showFull ? 'Ver menos' : 'Continuar leyendo'}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={showFull ? 'is-up' : ''}>
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {socialIcons.length > 0 && (
          <ul className="mt-artist__socials">
            {socialIcons.map((s) => (
              <li key={s.k}>
                <a href={s.href} target="_blank" rel="noopener noreferrer">{s.label}</a>
              </li>
            ))}
          </ul>
        )}
      </section>

      {latestRelease && (
        <section className="mt-artist__release">
          <div className="mt-container mt-artist__releaseHead">
            <span className="mt-eyebrow">On Rotation · Latest Release</span>
            <h2>
              Su último <em>capítulo.</em>
            </h2>
          </div>

          <div className="mt-container mt-artist__releaseGrid">
            <a
              className="mt-artist__releaseArt"
              href={latestRelease.appleUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Listen to ${latestRelease.title}`}
            >
              <img
                src={latestRelease.artworkLarge || latestRelease.artworkMedium}
                srcSet={
                  latestRelease.artworkMedium
                    ? `${latestRelease.artworkMedium} 600w, ${latestRelease.artworkLarge} 1200w`
                    : undefined
                }
                alt={`${latestRelease.title} artwork`}
              />
              <span className="mt-artist__releaseTag">
                <span>Escuchar en Apple Music</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </a>

            <div className="mt-artist__releaseInfo">
              <span className="mt-kicker">{latestRelease.type || 'Release'}</span>
              <h3>{latestRelease.title}</h3>
              <dl className="mt-artist__releaseMeta">
                <div>
                  <dt>Release date</dt>
                  <dd>{formatReleaseDate(latestRelease.releaseDate)}</dd>
                </div>
                <div>
                  <dt>Tracks</dt>
                  <dd>{latestRelease.trackCount}</dd>
                </div>
                {latestRelease.genre && (
                  <div>
                    <dt>Género</dt>
                    <dd>{latestRelease.genre}</dd>
                  </div>
                )}
              </dl>

              {allReleases.length > 1 && (
                <div className="mt-artist__discography">
                  <span className="mt-kicker">Discografía reciente</span>
                  <ul>
                    {allReleases.slice(0, 5).map((r) => (
                      <li key={r.id}>
                        <a href={r.appleUrl} target="_blank" rel="noopener noreferrer">
                          <span>{String(new Date(r.releaseDate).getFullYear()) || '—'}</span>
                          <em>{r.title}</em>
                          <small>{r.type}</small>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section id="music" className="mt-artist__music">
        <div className="mt-container mt-artist__musicHead">
          <span className="mt-eyebrow">On Rotation</span>
          <h2>
            Música <em>en vivo.</em>
          </h2>
        </div>
        <div className="mt-container mt-artist__players">
          {youtubeEmbed && (
            <div className="mt-artist__player mt-artist__player--video">
              <span className="mt-artist__playerLabel">YouTube</span>
              <div className="mt-artist__playerFrame">
                <iframe
                  title={`${name} · YouTube`}
                  src={`https://www.youtube.com/embed/${youtubeEmbed}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
          {spotifyEmbed && (
            <div className="mt-artist__player mt-artist__player--spotify">
              <span className="mt-artist__playerLabel">Spotify</span>
              <div className="mt-artist__playerFrame mt-artist__playerFrame--spotify">
                <iframe
                  title={`${name} · Spotify`}
                  src={spotifyEmbed}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section id="tour" className="mt-artist__tour">
        <div className="mt-container mt-artist__tourHead">
          <span className="mt-eyebrow">On Tour</span>
          <h2>Próximas <em>fechas.</em></h2>
        </div>
        <div className="mt-container mt-artist__tourBody">
          <p>No hay eventos programados. Regresa pronto.</p>
          <a href={`mailto:${booking}`} className="mt-btn mt-btn--ghost">
            <span>Contactar para fechas</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </section>
    </article>
  );
}

export default ArtistProfile;
